
import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';

import { selectRecipeCreationOption } from '../../app/homepage/homepage.slice';
import { setLoadingRecipeCreating } from '../../app/recipeCreation/createRecipe.slice';
import { getDuplicateRecipeSearchList } from '../../app/search/search.thunk';
import { SearchHit } from '../../app/search/search.types';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import useRecipeCreation from '../../hooks/useRecipeCreation';
import { CloseIcon } from '../../iconRef/recipePageIcons';
import { ErrorManagementContext } from '../../utils/errorManagement/ErrorManagementProvider';
import { EventLogsEnum } from '../../utils/events';
import { resizeImageIfCloudinary } from '../../utils/images/cloudinaryUtils';
import LoaderButton from '../buttons/loaderButton';
import LoadingUpAnimation from '../LoadingUpAnimation';
import ListCard from '../searchPage/listCard';
import SearchRecipe from '../searchPage/searchRecipe';
import styles from "./recipeCreateForm.module.css";
import { DEFAULT_IMG } from './utils/constants';

const AllRecipeCard = ({ option, handleUserLevelRecipes }: { option: SearchHit, handleUserLevelRecipes: (recipe: SearchHit) => void }) => {
    const defaultImage = DEFAULT_IMG;
    if (option?.metadata?.name) {
        return (
            <ListCard
                image={resizeImageIfCloudinary(option.metadata.url ? option?.metadata?.url : defaultImage, 48, 48, 48, 48, true)}
                key={option.id}
                onClick={() => handleUserLevelRecipes(option)}
                title={option?.metadata?.name}
            />
        )
    }
    return null
}

const DuplicateRecipeForm = () => {
    const dispatch = useAppDispatch()
    const history = useHistory();
    const { duplicateRecipe, loading: savingRecipe } = useRecipeCreation();
    const [recipeName, setRecipeName] = useState<string>('');
    const { cookClubLog } = useContext(ErrorManagementContext);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const searchFilteredRecipes = useAppSelector((state) => state.search.searchFilteredRecipes);
    const [recipeID, setRecipeID] = useState<string>('');
    const [error, setError] = useState(false)
    const { logRecipeEdit } = useLogRecipeEdit();

    const [loderDisplay, setLoaderDisplay] = useState<boolean>(false)

    const [recipeImg, setRecipeImg] = useState<string>('');

    const handleClose = () => {
        setError(false);
        dispatch(selectRecipeCreationOption(null));
        dispatch(setLoadingRecipeCreating(false));
        history.push("/");
    }

    const onCompleted = (recipeId: string) => {
        history.push(`/recipe/${recipeId}`);
        dispatch(selectRecipeCreationOption(null));
    }

    const onCreate = () => {
        if (!recipeName) {
            setError(true)
            return;
        }
        logRecipeEdit(EventLogsEnum.APP_RECIPE_CREATE_DUPLICATE, {
            eventData: { recipeID }
        })
        duplicateRecipe(recipeID, `${recipeName} - Copy`, (recipeId) => onCompleted(recipeId), (err) => cookClubLog(`Something went wrong. Try Again!`, "", {}, { ui: { type: "info" }, browserConsole: true }));
    }

    const debouncedQuerySearchService = useCallback(debounce((queryString: string) => dispatch(getDuplicateRecipeSearchList({ queryString })), 500), [dispatch])
    const typeHandler = useCallback((queryString: string) => {
        if (!isSearchModalOpen) setIsSearchModalOpen(true)
        setRecipeName(queryString);
        debouncedQuerySearchService(queryString)
    }, [isSearchModalOpen]);

    const handleUserLevelRecipes = (recipe: SearchHit) => {
        setRecipeID(recipe.id);
        setRecipeName(recipe.metadata.name)
        setIsSearchModalOpen(false);
        setLoaderDisplay(true)
        setRecipeImg(recipe.metadata.url)

    };
    const renderInputComponent = (params: TextFieldProps) => (
        <SearchRecipe
            {...params}
            recipeImg={recipeImg}
            setLoaderDisplay={setLoaderDisplay}
            setRecipeImg={setRecipeImg}
            setRecipeName={setRecipeName}
            typeHandler={typeHandler}
        />
    );

    if (savingRecipe) {
        return <LoadingUpAnimation />

    }

    return (
        <Grid className={styles.main_grid} spacing={2}>
            <Grid container direction="row">

                <Grid className={styles.heading_style} item xs={6}  >
                    <Typography variant='body3'>Duplicate Recipe</Typography>
                </Grid>
                <Grid className={styles.close_btn} item xs={6}  >

                    <IconButton onClick={handleClose}><CloseIcon /></IconButton>
                </Grid>
            </Grid>
            <Typography variant='caption3Light'>Choose a recipe, duplicate and modify</Typography>

            <Autocomplete
                className={styles.duplicate_form_auto_complete}

                disablePortal
                filterOptions={(option) => option}
                getOptionLabel={(option) => option?.metadata?.name}
                inputValue={recipeName}
                ListboxProps={{ style: { minHeight: 'inherit' } }}
                onClose={() => setIsSearchModalOpen(false)}
                open={isSearchModalOpen}
                options={searchFilteredRecipes}

                renderInput={renderInputComponent}
                renderOption={(props, option) => (
                    <div className={styles.recipe_card}>
                        <AllRecipeCard handleUserLevelRecipes={handleUserLevelRecipes} option={option} />
                    </div>
                )}
            />
            {
                loderDisplay && <LoaderButton disabled={savingRecipe} loading={savingRecipe} onClick={onCreate}
                    text="Duplicate" />
            }
        </Grid >
    )
}

export default DuplicateRecipeForm;